export type ClientErrorReviewStatus = "unreviewed" | "investigating" | "fixed";

const STATUS_KEY = "tt-analytics-client-error-review-v1";
const NOTES_KEY = "tt-analytics-client-error-notes-v1";

/** For `storage` listeners (cross-tab sync). */
export const CLIENT_ERROR_REVIEW_STORAGE_KEY = STATUS_KEY;
export const CLIENT_ERROR_NOTES_STORAGE_KEY = NOTES_KEY;

function readStatusMap(): Record<string, ClientErrorReviewStatus> {
  try {
    const raw = localStorage.getItem(STATUS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as Record<string, ClientErrorReviewStatus>;
  } catch {
    return {};
  }
}

function writeStatusMap(m: Record<string, ClientErrorReviewStatus>): void {
  localStorage.setItem(STATUS_KEY, JSON.stringify(m));
}

function readNotesMap(): Record<string, string> {
  try {
    const raw = localStorage.getItem(NOTES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as Record<string, string>;
  } catch {
    return {};
  }
}

function writeNotesMap(m: Record<string, string>): void {
  localStorage.setItem(NOTES_KEY, JSON.stringify(m));
}

export function getClientErrorReviewStatus(eventId: string): ClientErrorReviewStatus {
  const v = readStatusMap()[eventId];
  return v === "investigating" || v === "fixed" ? v : "unreviewed";
}

export function setClientErrorReviewStatus(
  eventId: string,
  status: ClientErrorReviewStatus,
): void {
  const m = readStatusMap();
  if (status === "unreviewed") {
    delete m[eventId];
  } else {
    m[eventId] = status;
  }
  writeStatusMap(m);
}

const ORDER: ClientErrorReviewStatus[] = ["unreviewed", "investigating", "fixed"];

/** Cycles: unreviewed → investigating → fixed → unreviewed. Returns the new status. */
export function cycleClientErrorReviewStatus(eventId: string): ClientErrorReviewStatus {
  const cur = getClientErrorReviewStatus(eventId);
  const i = ORDER.indexOf(cur);
  const next = ORDER[(i + 1) % ORDER.length]!;
  setClientErrorReviewStatus(eventId, next);
  return next;
}

/** Emoji for investigated / fixed only. Unreviewed is shown as a red “?” in the UI (see EventsPanel). */
export function clientErrorReviewIcon(status: ClientErrorReviewStatus): string {
  switch (status) {
    case "investigating":
      return "🔎";
    case "fixed":
      return "✅";
    default:
      return "?";
  }
}

export function reviewStatusTooltip(status: ClientErrorReviewStatus): string {
  switch (status) {
    case "investigating":
      return "Investigating — click to mark fixed (saved in this browser only)";
    case "fixed":
      return "Fixed — click to clear back to unreviewed";
    default:
      return "Unreviewed — click to mark investigating (saved in this browser only)";
  }
}

export function reviewStatusLabel(status: ClientErrorReviewStatus): string {
  switch (status) {
    case "investigating":
      return "Investigating";
    case "fixed":
      return "Fixed";
    default:
      return "Unreviewed";
  }
}

export function getClientErrorNote(eventId: string): string {
  return readNotesMap()[eventId] ?? "";
}

export function setClientErrorNote(eventId: string, note: string): void {
  const m = readNotesMap();
  const trimmed = note.trim();
  if (!trimmed) {
    delete m[eventId];
  } else {
    m[eventId] = trimmed.slice(0, 4000);
  }
  writeNotesMap(m);
}
